<template>
<div :class="{container: true, editing: editing}">
  <div class="content" ref="fullcontent" >
    <filters-display
      v-if="!editing"
      ref="filter"
      :filter="filter"
      :canEdit="canEdit || false"
      :useGlobalFilters="useGlobalFilters"
      @change="updateFilters" />
    <el-row 
      v-loading="loading"
      type="flex" 
      class="graph-area">
      <el-col 
        ref="graphbox" 
        :span="graphboxSize" 
        :class="['graph', isValid ? graph.id : null]">
        <component
          :is="graph.component"
          v-if="isValid"
          ref="graph"
          :data="formatedData"
          :width="width"
          :height="height"
          :x-label-rotate="-70"
          :margin-bottom="100"
          :showLegend="false"
          :margin-left="60"
          @select="handleSelect"
          @unselect="handleUnselect"
          v-bind="props" />
        <template v-else-if="message" >
          {{ message }}
        </template>
        <template v-else>
          Configuration en cours
        </template>
      </el-col>
    </el-row>
    <em v-if="limit" >Seuls les {{ limit }} premiers résultats sont affichés</em>
    <el-input
      v-if="editing"
      v-model="description"
      type="textarea"
      :rows="2"
      placeholder="Description" />
    <div 
      v-else 
      class="description">{{ description }}</div>
    <div 
      v-if="!editing && graph && dimension.length > graph.dimensions && graph.dimensions" 
      class="groups">
      <el-checkbox-group
        v-model="activeDimensions">
        <el-checkbox
          v-for="(d) in dimension"
          :key="api.dimension.uniqueName(d.value)"
          :label="api.dimension.uniqueName(d.value)">
          Répartition par {{ api.dimension.name(d.value) }}
        </el-checkbox>
      </el-checkbox-group>
    </div>
    
    <div ref="legend" class="legend-container"
         v-if="$refs.graph && $refs['graph'].keyColors">
      <div class="legend">
        <div
          v-for="({label, color}) in $refs['graph'].keyColors.slice( (capturing ? 0 : legendPage * computedLegendPageSize), (capturing ? undefined : (legendPage + 1) * computedLegendPageSize))"
          :key="label">
          <span
            class="legend__color"
            :style="{background: color}">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
          {{ label }}
        </div>
      </div>
      <el-pagination 
        v-if="!capturing && $refs['graph'].keyColors && $refs['graph'].keyColors.length > computedLegendPageSize"
        small
        layout="prev, pager, next"
        @current-change="(p) => legendPage = p - 1"
        :page-size="computedLegendPageSize"
        :total="$refs['graph'].keyColors.length">
      </el-pagination>
    </div>
  </div>
</div>
</template>

<script>
import {api, GrantedCondition} from 'macarons'
import * as d3 from 'd3'
import {Registry} from './graph'
import EventBus from './events'
import CustomGraphEdit from './CustomGraphEdit'
import Editable from './Editable'
import { capture, copy, rules } from './capture'
import download from './download'
import config from './config'
import FiltersDisplay from './components/FiltersDisplay'
import globalFilterAware from './mixins/globalFilterAware'
import customGraphApiAware from './mixins/customGraphApiAware'

export default {
  components: {
    FiltersDisplay
  },
  extends: Editable,
  mixins: [
    globalFilterAware,
    customGraphApiAware
  ],
  props: {
    widget: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      message: null,
      exportSrc: null,
      updateDataDebounce: null,
      legendPosition: null,
      legendPage: 0,
      graph: null,
      measure: [],
      title: null,
      description: null,
      data: [],
      props: {},
      metadata: null,
      activeDimensions: [],
      noEmpty: false,
      limit: 0,
      limitStrict: false,
      legendPageSize: null,
      advanced: null,
      specialConfig: null,
      colorScheme: null,
      formats: null,
      ready: false,
      loading: false,
      dimension: [],
      editable: CustomGraphEdit,
      capturing: false
    }
  },
  computed: {
    computedLegendPageSize () {
      return this.legendPageSize ? this.legendPageSize : Math.max(2, Math.floor(this.width / 300))
    },
    graphboxSize () {
      return this.editing && config.resizeGraphAreaOnEditing ? 18 : 24
    },
    formatLocale () {
      return d3.formatLocale({
        decimal: ',',
        thousands: ' ',
        grouping: [3],
        currency: '€'
      })
    },
    isQueryValid () {
      if (!this.graph) {
        return false
      }
      if (!this.dimension.length && this.graph.dimensions) {
        return false
      }
      if (!this.measure.length) {
        return false
      }
      if (this.activeDimensions.length < this.graph.dimensions) {
        return false
      }
      return true
    },
    formatedData () {
      if (this.graph === null || !this.measure.length || this.metadata === null) {
        return null
      }
      return this.graph.adaptater(this.data, this)
    },
    measures () {
      return this.$store.getters['customGraph/measures'].map(m => {
        m = JSON.parse(JSON.stringify(m))
        if (this.dimension.length) {
          for (const d of this.dimension) {
            const index = this.api.measure.dimensions(m).findIndex(dm => {
              return dm === this.api.dimension.uniqueName(d.value).substr(0, dm.length)
            })
            if (index === -1) {
              m.disabled = true
              return m
            }
          }
          m.disabled = false
        } else {
          m.disabled = false
        }
        return m
      })
    },
    dimensions () {
      return this.$store.getters['customGraph/dimensions'].map(d => {
        d = JSON.parse(JSON.stringify(d))
        for (const m of this.measure) {
          const index = this.api.measure.dimensions(m).findIndex(dm => {
            return dm === this.api.dimension.uniqueName(d).substr(0, dm.length)
          })
          if (index === -1) {
            d.disabled = true
            return d
          }
        }
        d.disabled = false
        return d
      })
    },
    isValid () {
      if (!this.graph) {
        return false
      }
      if (this.data === null) {
        return false
      }
      if (this.formatedData === null) {
        return false
      }
      if (this.graph.id === 'stacked-bar' && typeof this.props.keys === 'undefined') {
        return false
      }
      return true
    }
  },
  watch: {
    advanced () {
      if (this.graph && this.graph.recomputeDataOnPropsChange) {
        this.updateData()
      }
    },
    specialConfig () {
      if (this.graph && this.graph.recomputeDataOnPropsChange) {
        this.updateData()
      }
    },
    isValid () {
      if (this.isValid) {
        this.setLegendPosition()
      }
    },
    graph () {
      while (this.activeDimensions.length > this.graph.dimensions) {
        this.activeDimensions.splice(-1)
      }
      if (this.activeDimensions.length < this.graph.dimensions) {
        for (const d of this.dimension) {
          if (this.activeDimensions.indexOf(this.api.dimension.uniqueName(d.value)) === -1) {
            this.activeDimensions.push(this.api.dimension.uniqueName(d.value))
            if (this.activeDimensions.length >= this.graph.dimensions) {
              break
            }
          }
        }
      }
      this.doSetMenu()
    },
    measure () {
      this.updateData()
    },
    dimension () {
      if (this.graph) {
        this.activeDimensions = this
          .dimension
          .slice(0, this.graph.dimensions)
          .map(d => this.api.dimension.uniqueName(d.value))
      }
    },
    activeDimensions (value, prev) {
      if (this.graph.dimensions && this.dimension.length) {
        let changed = false
        let active = [].concat(this.activeDimensions)
        let first = this.api.dimension.uniqueName(this.dimension[0].value)
        while (active.length > this.graph.dimensions) {
          active.shift()
          changed = true
        }
        while (active.length < this.graph.dimensions) {
          active.push(prev.pop() || first)
          changed = true
        }
        if (!active.includes(first) && this.graph.dimensions > 1) {
          active.push(first)
          changed = true
          if (active.length > this.graph.dimensions) {
            active.shift()
          }
        }
        if (changed) {
          active.unshift(active.splice(active.indexOf(first), 1)[0])
          this.activeDimensions = active
          return
        }
      }
      this.updateData()
      this.saveState()
    },
    idQueryValid () {
      this.doSetMenu()
    },
    title () {
      this.$emit('titleChange', this.title)
    }
  },
  mounted () {
    this.load()
    this.computeSize()
    this.$parent.$on('resize', this.computeSize)
  },
  created () {
    EventBus.$on('graph:edit', this.computeSize)
    EventBus.$on('graph:edit', this.doSetMenu)
    EventBus.$on('graph:cancel', this.computeSize)
    EventBus.$on('graph:cancel', this.doSetMenu)
    this.$store.commit('customGraph/addEventListener', {
      event: 'refresh',
      handler: this.updateData
    })
  },
  beforeDestroy () {
    EventBus.$off('graph:edit', this.computeSize)
    EventBus.$off('graph:edit', this.doSetMenu)
    EventBus.$off('graph:cancel', this.computeSize)
    EventBus.$off('graph:cancel', this.doSetMenu)
    this.$parent.$off('resize', this.computeSize)
    this.$store.commit('customGraph/removeEventListener', {
      event: 'refresh',
      handler: this.updateData
    })
    clearTimeout(this.updateDataDebounce)
  },
  methods: {
    exportToPng () {
      this.capturing = true
      this.$nextTick(() => {
        const node = copy(this.$refs.fullcontent)
        node.style.margin = 0
        node.style.padding = 0
        
        const title = document.createElement('h2')
        title.appendChild(document.createTextNode(this.title))
        title.style.fontFamily = 'Montserrat'
        title.style.padding = '1em'
        title.style.margin = 0
        title.style.position = 'absolute'
        title.style.top = 0
        node.insertBefore(title, node.firstElementChild)
        
        if (this.$refs.legend) {
          const box = this.$refs.fullcontent.getBoundingClientRect()
          const legend = node.querySelector('.legend-container')
          legend.style.top = box.height +'px'
          legend.style.position = 'absolute'
          legend.style.color = "#000"
          legend.querySelectorAll('.legend div').forEach(node => {
            if (node.style) {
              node.style.color = "#000"
              node.style.fontWeight = '900'
            }
          })
        }
        
        capture(node, 'png', {rules: rules()})
          .then(img => download(img, this.title + '.png'))
          .then(() => {
            this.capturing = false
          })
      })
    },
    doSetMenu () {
      if (this.graph && !this.graph.hideExport) {
        const condition = new GrantedCondition('EXPORTS')
        condition.resolve().then(grant => {
          if (grant) {
            this.setMenu([{
              label: 'Exports',
              value: 'exports',
              children: [
                {label: 'Simple', value: this.exportData},
                {label: 'Avancé', value: this.exportDataAdvanced},
                {label: 'Image', value: this.exportToPng}
              ]
            }])
          } else {
            this.setMenu()
          }
        })
      } else {
        this.setMenu()
      }
    },
    filterGroup (key) {
      return 'Group' + (this.graph.filterGroupIndex || 0) + (key || 'UniqueName')
    },
    isSelectAllowed () {
      if (typeof this.$store.state.board.data.allowFilterByGraph === 'undefined') {
        return true
      }
      return this.$store.state.board.data.allowFilterByGraph
    },
    handleSelect (data) {
      if (this.isSelectAllowed()) {
        EventBus.$emit('add-filter', {
          filter: data.data[this.filterGroup()],
          filterName: data.data[this.filterGroup('Caption')],
          source: this.id,
          data: data
        })
      }
    },
    handleUnselect (data) {
      if (this.isSelectAllowed()) {
        EventBus.$emit('remove-filter', {
          filter: data.data[this.filterGroup()],
          filterName: data.data[this.filterGroup('Caption')],
          source: this.id,
          data: data
        })
      }
    },
    handleExport (mode) {
      switch (mode) {
      case 'simple':
        return this.exportData()
        
      case 'advanced':
        return this.exportDataAdvanced()
      }
    },
    load () {
      this.ready = false
      this.$store.dispatch('customGraph/loadMeasures')
      this.$store.dispatch('customGraph/loadDimensions')
      return api.get('custom-graph/widget/' + this.widget.id).then(response => {
        for (const g of Registry.asArray()) {
          if (g.id === response.data.graph) {
            this.graph = g
            break
          }
        }
        this.id = response.data.id
        this.title = response.data.title
        this.description = response.data.description
        this.noEmpty = response.data.noEmpty
        this.limit = response.data.limit || 0
        this.limitStrict = response.data.limitStrict || false
        this.legendPageSize = response.data.legendPageSize || null
        this.advanced = response.data.advanced
        this.specialConfig = response.data.specialConfig
        this.colorScheme = JSON.parse(response.data.colorScheme || null)
        this.formats = response.data.formats ? JSON.parse(response.data.formats) : []
        Promise.all([
          this.loadState(response.data.id),
          this.loadDimensions(response.data.dimension),
          this.loadMeasure(response.data.measure),
          this.loadFilters(response.data.filter)
        ]).then(responses => {
          if (responses[0] !== null) {
            this.setFilterState(responses[0].filters)
            this.activeDimensions.splice(0, this.activeDimensions.length, ...responses[0].dimensions)
          } else {
            this.setFilters(true)
          }
          this.computeSize(response.data.legendPosition)
        })
      }).catch(() =>{ 
        this.ready = true
        this.computeSize()
      })
    },
    loadMeasure (measure) {
      return this.$store.dispatch('customGraph/loadMeasures').then(() => {
        if (!this.graph) {
          this.measure.splice(0)
        } else {
          this.measure.splice(0, this.measure.length, ...this.measures.filter(m => {
            return measure.indexOf(this.api.measure.uniqueName(m)) !== -1
          }))
          this.measure.sort(function(a, b){  
            return measure.indexOf(
              this.api.measure.uniqueName(a) - measure.indexOf(this.api.measure.uniqueName(b))
            );
          });
        }
      })
    },
    loadDimensions (dimensions) {
      return Promise
        .all(dimensions.map(d => this.$store.dispatch('customGraph/loadDimension', d)))
        .then(() => {
          this.dimension.splice(0, this.dimension.length, ...dimensions.map(d => {
            return {
              value: this.$store.getters['customGraph/dimension'](d),
              options: {}
            }
          }).filter(d => typeof d.value !== 'undefined'))
        })
    },
    buildQuery () {
      const filters = {}
      const data = this.useGlobalFilters ? this.globalFilters : [].concat(...this.filter.map(f => {
        return (typeof f.active === 'undefined' ? f.defaultValues : f.active).map(a => a.uniqueName)
      }))
      data.forEach(f => {
        filters[f] = f
      })
      const query = {
        measures: Array.isArray(this.measure) ? this.measure.map(m => {
          return this.api.measure.uniqueName(m)
        }) : this.api.measure.uniqueName(this.measure),
        groups: Array.from(new Set(this.activeDimensions)),
        filters: Object.keys(filters)
      }
      if (this.graph && this.graph.queryHandler) {
        this.graph.queryHandler(query, this)
      }
      return query
    },
    updateData () {
      if (!this.isQueryValid) {
        return
      }
      clearTimeout(this.updateDataDebounce)
      this.updateDataDebounce = setTimeout(() => {
        this.doSetMenu()
        this.loading = true
        this.$store.dispatch('customGraph/execute', this.buildQuery()).then(data => {
          this.message = data.message || null
          this.metadata = data.metadata
          this.data.splice(0, this.data.length, ...data.data)
        }).catch(err => {
          console.log(err)
        }).finally(() => {
          this.$nextTick(() => {
            this.loading = false
            this.computeSize()
          })
        })
      }, 200)
    },
    computeSize (legendPosition) {
      this.width = 0
      this.height = 0
      this.$nextTick(() => {
        if (typeof this.$refs.graphbox !== 'undefined') {
          const bbox = this.$refs.graphbox.$el.getBoundingClientRect()
          this.width = bbox.width * 0.99
          this.height = bbox.height * 0.99
        }
        this.setLegendPosition(legendPosition)
      })
    },
    save () {
      let legendPosition
      if (typeof this.$refs.graph !== 'undefined' && typeof this.$refs.graph.getLegendPosition !== 'undefined') {
        legendPosition = this.$refs.graph.getLegendPosition()
        this.$refs.graph.setLegendPosition(legendPosition)
      } else {
        legendPosition = null
      }
      this.editing = false
      this.computeSize()
      return api.post('custom-graph/widget/' + this.widget.id, {
        title: this.title,
        description: this.description,
        graph: this.graph.id,
        measure: Array.isArray(this.measure) ? this.measure.map(
          m => this.api.measure.uniqueName(m)
        ) : [this.api.measure.uniqueName(this.measure)],
        dimension: this.dimension.map(d => this.api.dimension.uniqueName(d.value)),
        filter: this.serializeFilter(),
        legendPosition: legendPosition ? {
          x: Math.round(legendPosition.x, 10),
          y: Math.round(legendPosition.y, 10),
          width: legendPosition.width,
          height: legendPosition.height
        } : null,
        noEmpty: this.noEmpty,
        limit: this.limit,
        limitStrict: this.limitStrict,
        legendPageSize: this.legendPageSize,
        advanced: this.advanced,
        specialConfig: this.specialConfig,
        colorScheme: JSON.stringify(this.colorScheme),
        formats: JSON.stringify(this.formats)
      }).then(() => {
        this.cancel()
      })
    },
    buildMeasureFormatter (name) {
      if (this.formats) {
        const format = this.formats.find(f => f.measure === name)
        if (format && format.format) {
          return this.formatLocale.format(format.format)
        }
      }
      const metadata = this.metadata.measures.find(m => this.api.measure.uniqueName(m) === name)
      if (!metadata) {
        return this.buildFormatter('')
      }
      return this.buildFormatter(metadata.formatString)
    },
    buildFormatter (formatString) {
      if (formatString === 'Percent') {
        return this.formatLocale.format('.3p')
      }
      if (formatString === '#,##0') {
        return this.formatLocale.format(',.0f')
      }
      if (formatString === '0') {
        return this.formatLocale.format('d')
      }
      return this.formatLocale.format(',.2f')
    },
    buildProps (props) {
      const defaultProps = {
        yTitle: this.metadata.measures.length ? this.api.measure.unit(this.metadata.measures[0]) : '',
        yFormatter: this.buildMeasureFormatter(
          this.api.measure.uniqueName(this.measure[0])
        ),
        yKey: this.measure.map(m => {
          return this.api.measure.name(m)
        }),
        xKey: 'Group0Caption'
      }
      if (typeof props !== 'undefined') {
        Object.keys(props).forEach(k => {
          defaultProps[k] = props[k]
        })
      }
      if (this.advanced) {
        try {
          const advancedProps = JSON.parse(this.advanced)
          if (typeof advancedProps === 'object') {
            Object.assign(defaultProps, advancedProps)
          }
        } catch (err) {}
      }
      if (this.specialConfig) {
        try {
          const specialConfigProps = JSON.parse(this.specialConfig)
          if (typeof specialConfigProps === 'object') {
            Object.assign(defaultProps, specialConfigProps)
          }
        } catch (err) {}
      }
      this.props = defaultProps
    },
    setLegendPosition (legendPosition) {
      if (typeof legendPosition !== 'undefined') {
        this.legendPosition = legendPosition
      }
      if (typeof this.$refs.graph !== 'undefined') {
        if (typeof this.$refs.graph.setLegendPosition !== 'undefined') {
          this.$refs.graph.setLegendPosition(this.legendPosition)
        }
      }
    },
    exportDataAdvanced () {
      this.exportData(true)
    },
    exportData (advanced) {
      if (!this.isQueryValid) {
        return
      }
      advanced = typeof advanced === 'undefined' ? false : advanced
      const query = this.buildQuery()
      query.route = advanced ? 'ExecuteQueryExcelFilter' : 'ExecuteQueryExcel'
      query.axios = {
        responseType: 'blob'
      }
      this.$store.dispatch('customGraph/DSNQuery', query).then(data => {
        window.open(window.URL.createObjectURL(new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })))
      })
    },
    saveState: function () {
      if (this.id !== null && this.ready) {
        api.post('custom-graph/' + this.id + '/state', {
          state: JSON.stringify({
            filters: this.filter,
            dimensions: this.activeDimensions
          })
        })
      }
    }
  }
}
</script>

<style scoped>
.legend {
    list-style-type: none;
    flex-wrap: wrap;
    text-align: left;
    padding: 1em 2em;
    font-size: 0.9em;
    display: flex;
}

.legend > * {
    width: 300px;
    padding: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    text-align: left;
    align-items: center;
    break-inside: avoid;
}
.legend__color {
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
    overflow: hidden;
    border-radius: 30px;
    margin-right: 5px;
}

.export-cta {
    color: rgb(64, 158, 255)!important;
    border: 1px solid rgba(192, 196, 204, 0.6);
    border-radius: 4px;
    padding: 10px;
    background: #FFF;
    font-weight: bold;
    cursor: pointer;
    justify-self: end;
}
.export-cta:active {
    border: 1px solid rgba(192, 196, 204, 1);
}

.container {
    background-color: #fff;
    display: flex;
    flex-direction: column;
    position: absolute;
    top:0;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0.4em;
}
.container.editing {
    box-shadow: inset 0 4px 5px #ccc;
}

.el-select, .el-cascader {
    width: 100%;
}
.content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}
.content .graph-area {
    flex: 1;
    min-height: 0;
}
.groups {
    display: flex;
}
.groups > div {
    padding: 10px;
}

.graph {
    position: relative;
}

.graph >>> .axis.y .domain, .graph >>> .axis.y2 .domain {
    display: none;
}

.graph >>> .axis.y line, .graph >>> .axis.y2 line{
    display: none;
}
.graph >>> .axis text, .graph >>> .legend text {
    fill: #5a5b5e;
    font-size: 11px;
}
.graph >>> .axis.x .tick line {
    stroke: #eceff1;
}

.graph >>> .axis.x .domain {
    stroke: #eceff1;
    fill: #f6f5fa;
}

.graph >>> .axis.x .tick:first-of-type line, .graph >>> .axis.x .tick:last-of-type line {
    display: none;
}

.options {
    min-height: 0;
    overflow-y: auto;
}
.description {
    white-space: pre-wrap;
}
</style>



// WEBPACK FOOTER //
// node_modules/macarons-graphs-front/src/CustomGraph.vue